/* External */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

/* Ott-common */
import {
  Npaw, Tracks, getCDNTokenizationConfig, hasTokenization, getSourceWithoutAds,
} from '@dtvgo/player-utils';
import { PLAYER_TRACKS_EVENTS, PlayerRequestType } from '@dtvgo/player-common-utils';
import { useSourceSelector } from '@dtvgo/player-utils-react';

/* Components */
import { PLAYER_LONG_PAUSE_GO_TO_LIVE_DURATION_MINUTES } from '~/utils/env';
import ShakaPlayer from './ShakaPlayer';
import PlayerUI from './PlayerUI';

/* Hooks */
import useIsDrmAgentLoaded from './utils/useIsDrmAgentLoaded';
import useIsReadyToPlay from './utils/useIsReadyToPlay';
import {
  getMatchingAudio,
  getMatchingSubtitle,
  useAudioAndSubtitlesPreference,
  hasAudioTrackByLanguage,
  getSubtitleTrackByLanguage,
} from '../../hooks/savedAudioAndSubtitlesHooks';
import useGetUpsellMessage from '../../hooks/useGetUpsellMessage';

/* Utils */
import { noOp } from '../../utils/functions';
import {
  DrmKeySystem,
  REPORT_GA,
  PLAYER_TYPES,
  Locale,
  PROFILE_VIEWED_INTERACTIVITY_BUTTON,
  PROFILES_WERE_SHOWN_INTERACTIVITY_BUTTON,
} from '../../utils/constants';
import {
  useConfig, useCountry, useLicenseRequestConfig, useIsBrazil,
} from '../../providers/ConfigProvider';
import { gaService } from '../../services/gaService';
import useAnalytics from './utils/useAnalytics';
import platform from '../../utils/platform';
import { useShowDisconnectedMessage } from '../../providers/ConnectionStatusProvider';
import { createProcessAudioTracks } from './PlayerUI/TracksControls/utils';
import { useLoading } from '../../providers/LoadingProvider';
import { useDeviceInfo } from '../../providers/DeviceInfoProvider';
import { useQueriedDrm } from '../../providers/QueryParamsProvider';
import {
  createConfigMatcher, logLicenseEvent, usePlayerConfig,
} from './utils';
import {
  formatContentData,
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
  playerType,
  transmissionContentType,
} from '../../utils/googleTags';
import { isSports } from '../../utils/metaDataUtils';
import parentalControlBlock from '../../utils/parentalControlUtils';
import { useGtmLocalContext } from '../../providers/GtmLocalProvider';
import { getProfileSelected, isTapppSDKActiveForOsVersion } from '../../utils/profiles';

/* Styles */
import './Player.scss';

const npawAdapterOptions = Npaw.getAdapterOptions(platform.platformName);
const TIME_TO_WAIT_ON_PAUSE = 200;

function Player({
  className = '',
  schedule = undefined,
  content = undefined,
  isContentRequestCompleted = true,
  bufferConfig = undefined,
  isLive = false,
  hasNextEpisode = false,
  savedProgress = 0,
  channelId = undefined,
  channelName = undefined,
  playSource = '',
  sources = undefined,
  drmKeySystem, // required prop
  goToNextEpisode = undefined,
  shouldReloadOnError = undefined,
  onPlaying = noOp,
  onPause = noOp,
  onProgress = noOp,
  onDurationChange = noOp,
  onSeeked = noOp,
  onBuffering = noOp,
  onBufferingEnded = noOp,
  onEnded = noOp,
  onHDCPError = undefined,
  onLicenseFailed = undefined,
  onError = noOp,
  isFromInterstitial = false,
  homeOnBack = false,
  isCurrentInterstitialSchedule = undefined,
  onUserInteraction = noOp,
  counterEpisode = 0,
  childrenWrapper = undefined,
  setPlayerReadyToPlay = undefined,
  onGoLive = undefined,
  children: childrenProp = undefined,
  hasPlaybackAuthorizerSuccess = undefined,
}, ref) {
  const {
    state: {
      transmissionType,
      sectionName,
      componentType,
      collectionName,
      gtmPlaySource,
      providerName,
      source: carouselSource,
    } = {},
  } = useLocation();

  const { sdkVersion } = useDeviceInfo() || {};
  const { osName } = platform;
  const queriedDrm = useQueriedDrm();
  const country = useCountry();
  const isBrazil = useIsBrazil();
  const profileSelected = useMemo(getProfileSelected, []);
  const { isBetAllowed } = profileSelected?.interactivities || {};
  const profilesAlreadyViewedActivityButton = useMemo(
    () => JSON.parse(
      localStorage.getItem(PROFILES_WERE_SHOWN_INTERACTIVITY_BUTTON),
    ) || [],
    [],
  );
  const profileStoraged = profilesAlreadyViewedActivityButton.find(
    (profile) => profile.id === profileSelected.id,
  );
  const showButtonAnimation = isBetAllowed && (profileStoraged === undefined
    || profileStoraged?.viewedButton < 3)
    && isTapppSDKActiveForOsVersion(osName, sdkVersion);

  const {
    player: { lowLatencyBufferConfig, lowLatency } = {},
    isFetching: isFetchingConfig,
  } = useConfig();

  const { isLoading: isLoadingPlayerConfig, config } = usePlayerConfig();

  const { upsellMessage, upsellLink } = useGetUpsellMessage();

  const configMatcher = useMemo(() => createConfigMatcher({
    isLive,
    osName,
    osVersion: sdkVersion,
    sourceProvider: content?.sourceProvider,
    country,
  }), [isLive, osName, sdkVersion, content?.sourceProvider, country]);

  const playerToUse = PLAYER_TYPES.SHAKA;

  const pauseOnShowControls = useMemo(
    () => configMatcher(config?.pauseOnShowControls, { player: playerToUse }),
    [config?.pauseOnShowControls, configMatcher, playerToUse],
  );

  const bufferOverrides = useMemo(
    () => configMatcher(config?.bufferOverrides, { player: playerToUse }),
    [config?.bufferOverrides, configMatcher, playerToUse],
  );

  const restrictions = useMemo(
    () => configMatcher(config?.restrictions, { player: playerToUse }),
    [config?.restrictions, configMatcher, playerToUse],
  );

  const abrRestrictions = useMemo(
    () => configMatcher(config?.abrRestrictions, { player: playerToUse }),
    [config?.abrRestrictions, configMatcher, playerToUse],
  );

  const singleAudioTrackLanguage = useMemo(
    () => configMatcher(config?.singleAudioTrackLanguage, { player: playerToUse }),
    [config?.singleAudioTrackLanguage, configMatcher, playerToUse],
  );

  const recoveryOptions = useMemo(
    () => configMatcher(config?.recoveryOptions, { player: playerToUse }),
    [config?.recoveryOptions, configMatcher, playerToUse],
  );

  const disableAds = useMemo(
    () => configMatcher(config?.disableAds, { player: playerToUse }),
    [config?.disableAds, configMatcher, playerToUse],
  );

  const stallMechanism = useMemo(
    () => configMatcher(config?.stallMechanism, { player: playerToUse }),
    [config?.stallMechanism, configMatcher, playerToUse],
  );

  /**
   * Allows to change the minimum time behind the real live edge the player can be.
   * When this is higher, the player have the chance to store more segments in buffer,
   * but also it is more delayed from the LIVE signal.
   */
  const presentationDelay = useMemo(
    () => configMatcher(config?.presentationDelay, { player: playerToUse }),
    [config?.presentationDelay, configMatcher, playerToUse],
  );

  const logLicenseEvents = useMemo(
    () => configMatcher(config?.logLicenseEvents, { player: playerToUse }),
    [config?.logLicenseEvents, configMatcher, playerToUse],
  );

  const audioTracksFilter = useMemo(
    () => configMatcher(config?.audioTracksFilter, { player: playerToUse }),
    [config?.audioTracksFilter, configMatcher, playerToUse],
  );

  const preferredKeySystem = queriedDrm || drmKeySystem;

  const licenseRequestConfig = useLicenseRequestConfig();
  const playerRef = useRef();
  const {
    preference,
    saveAudioPreference,
    saveSubtitlesPreference,
  } = useAudioAndSubtitlesPreference();
  const showDisconnectedMessage = useShowDisconnectedMessage();
  const playingOnDisconnect = useRef(false);

  const isDrmAgentLoaded = useIsDrmAgentLoaded(preferredKeySystem);
  const { isReadyToPlay, resetIsReadyToPlay, handleReadyToPlay } = useIsReadyToPlay();

  const playbackAfterInterstitialRef = useRef(false);

  useAnalytics(isReadyToPlay, content, playSource);

  const isParentalBlocked = parentalControlBlock(content);

  const { hideLoading } = useLoading();

  const [isPlaying, setIsPlaying] = useState(false);
  const isPlayingRef = useRef(isPlaying);
  const playbackStarted = useRef(false);
  const progressRef = useRef(0);
  const [isSeeked, setIsSeeked] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [isSourceUnloaded, setIsSourceUnloaded] = useState(false);
  const [audioTracks, setAudioTracks] = useState([]);
  const [subtitleTracks, setSubtitleTracks] = useState([]);
  const [sourceToUse, setSourceToUse] = useState(undefined);
  const [reducedSize, setReducedSize] = useState(false);
  const [shouldPlay, setShouldPlay] = useState(false);
  const [gtmEventCategory, setGtmEventCategory] = useState(undefined);
  const isDisplayingMiniEpg = useRef(false);
  const subtitleTrack = useRef(undefined);
  const audioTrack = useRef(undefined);
  const startProgressBar = useRef(0);

  const { commonTaggingEvent } = useGtmLocalContext();

  const getProgress = useCallback(() => progressRef.current, []);
  const {
    source: baseSource, nextSource, startOffset,
  } = useSourceSelector(sources, { getProgress });

  const source = useMemo(() => {
    if (!baseSource || !disableAds) return baseSource;
    return getSourceWithoutAds(baseSource);
  }, [baseSource, disableAds]);

  const recovery = useMemo(() => ({
    ...recoveryOptions,
    reload: {
      ...recoveryOptions?.reload,
      source: baseSource && getSourceWithoutAds(baseSource),
    },
  }), [recoveryOptions, baseSource]);

  useEffect(() => {
    setPlayerReadyToPlay(isReadyToPlay);
  }, [isReadyToPlay, setPlayerReadyToPlay]);

  useEffect(() => {
    if (content) {
      if ((content?.type === 'EPISODE' || hasNextEpisode) && !isLive) {
        setGtmEventCategory(GTM_SUBCATEGORIES.PLAYER.SERIE);
      } else if (isLive) {
        setGtmEventCategory(GTM_SUBCATEGORIES.PLAYER.LIVE);
      } else {
        setGtmEventCategory(GTM_SUBCATEGORIES.PLAYER.MOVIE);
      }
    }
  }, [content, content?.type, hasNextEpisode, isLive]);

  const tagsMountRef = useRef(false);
  const isSubtitleChangedRef = useRef(false);

  const handleSubtitlePreference = useCallback(() => {
    const selectedTrack = subtitleTracks?.filter((track) => track.isSelected);
    subtitleTrack.current = selectedTrack[0] ? selectedTrack[0].iso : 'no-subtitle';
  }, [subtitleTracks]);

  useEffect(() => {
    handleSubtitlePreference();
  }, [handleSubtitlePreference]);

  const handleAudioPreference = useCallback(() => {
    const { audio } = preference;
    const selectedTrack = audioTracks?.filter((track) => track.isSelected);
    audioTrack.current = selectedTrack[0] ? selectedTrack[0].iso : audio?.iso;
  }, [audioTracks, preference]);

  useEffect(() => {
    handleAudioPreference();
  }, [handleAudioPreference]);

  const getLiveProgress = useCallback(() => (
    (schedule?.startTime ? (Date.now() - schedule.startTime) / 1000 : 0) + progressRef.current
  ), [schedule?.startTime]);

  const playerCommonTagEvent = useCallback(({ eventName, subcategory }) => {
    if (eventName === GTM_EVENT_NAMES.PLAYER.NEXT_EPISODE
      || eventName === GTM_EVENT_NAMES.PLAYER.NEXT_EPISODE_LOAD) {
      tagsMountRef.current = false;
    }

    commonTaggingEvent({
      eventName,
      subCategory: subcategory || gtmEventCategory,
      category: GTM_CATEGORIES.PLAYER_MOVIE_SERIE_LIVE_DATA,
      data: {
        content: {
          ...formatContentData({
            content,
            rest: {
              progress: isLive
                ? getLiveProgress()
                : progressRef.current,
              startProgressBar: startProgressBar?.current,
              playSource: gtmPlaySource,
              channelId,
              channelName,
              isLive,
            },
          }),
        },
        audioTrack: audioTrack?.current,
        subtitleTrack: subtitleTrack?.current,
        transmissionType: transmissionContentType(transmissionType),
        playerType: playerType(content?.type, isLive, isSports(content)),
        sectionName,
        componentType,
        collectionName,
        isSports: isSports(content),
        isLive,
        providerName,
        source: carouselSource?.toString()?.toLowerCase() ?? '',
      },
    });
  }, [
    commonTaggingEvent,
    content,
    gtmEventCategory,
    startProgressBar,
    gtmPlaySource,
    channelId,
    channelName,
    transmissionType,
    sectionName,
    componentType,
    collectionName,
    subtitleTrack,
    audioTrack,
    isLive,
    providerName,
    carouselSource,
    getLiveProgress,
  ]);

  useEffect(() => {
    if (gtmEventCategory && country && !tagsMountRef.current && isReadyToPlay) {
      startProgressBar.current = isLive ? getLiveProgress() : savedProgress || 0;
      playerCommonTagEvent({ eventName: GTM_EVENT_NAMES.PAGE_VIEW });
      tagsMountRef.current = true;
    }
  }, [
    country,
    getLiveProgress,
    gtmEventCategory,
    isLive,
    isReadyToPlay,
    playerCommonTagEvent,
    savedProgress,
  ]);

  useEffect(() => {
    if (isSubtitleChangedRef.current) {
      playerCommonTagEvent({
        subcategory: GTM_SUBCATEGORIES.PLAYER.AUDIO_AND_SUBTITLES,
        eventName: GTM_EVENT_NAMES.PLAYER.CHANGE_SUBTITLE,
      });
      isSubtitleChangedRef.current = false;
    }
  }, [playerCommonTagEvent]);

  const newKeySystemsConfig = useMemo(() => {
    const { keySystemsConfig } = source || {};
    const preferredKeySystems = preferredKeySystem ? [preferredKeySystem.toLowerCase()] : undefined;
    return {
      ...keySystemsConfig,
      preferredKeySystems,
      licenseRequest: licenseRequestConfig,
    };
  }, [licenseRequestConfig, preferredKeySystem, source]);

  const pauseTime = useRef(undefined);

  const goToLive = useCallback(() => {
    if (playerRef.current?.goToLive) {
      playerRef.current.goToLive();
      onGoLive?.();
    }
  }, [onGoLive]);

  const play = useCallback(() => {
    if (playerRef.current?.play) {
      if (isLive
        && (
          Date.now() - pauseTime.current
          > PLAYER_LONG_PAUSE_GO_TO_LIVE_DURATION_MINUTES * 60 * 1000
        )
      ) {
        goToLive();
      }
      playerRef.current.play();
    }
  }, [goToLive, isLive]);

  const pause = useCallback(() => {
    if (playerRef.current?.play) playerRef.current.pause();
  }, []);

  const scrubTo = useCallback((newProgress) => {
    if (playerRef.current?.setProgress) playerRef.current.setProgress(newProgress);
  }, []);

  const selectAudioTrack = useCallback(({ track }) => {
    if (playerRef.current?.selectAudioTrack) {
      saveAudioPreference(track);
      playerRef.current.selectAudioTrack(track);
      playerCommonTagEvent({
        subcategory: GTM_SUBCATEGORIES.PLAYER.AUDIO_AND_SUBTITLES,
        eventName: GTM_EVENT_NAMES.PLAYER.CHANGE_AUDIO,
      });
    }
  }, [playerCommonTagEvent, saveAudioPreference]);

  const selectSubtitleTrack = useCallback(({ track }) => {
    if (playerRef.current?.selectSubtitleTrack) {
      saveSubtitlesPreference(track);
      playerRef.current.selectSubtitleTrack(track);
      subtitleTrack.current = track?.iso;
      isSubtitleChangedRef.current = true;
    }
  }, [saveSubtitlesPreference]);

  const disableSubtitles = useCallback(() => {
    if (playerRef.current?.disableSubtitles) {
      saveSubtitlesPreference();
      playerRef.current.disableSubtitles();
      subtitleTrack.current = 'no-subtitle';
      isSubtitleChangedRef.current = true;
    }
  }, [saveSubtitlesPreference]);

  const pauseTimeout = useRef(undefined);

  const handlePlay = useCallback(() => {
    if (pauseTimeout.current) {
      clearTimeout(pauseTimeout.current);
      pauseTimeout.current = undefined;
    }
  }, []);

  const handlePlaying = useCallback(() => {
    pauseTime.current = undefined;
    playbackStarted.current = true;
    isPlayingRef.current = true;
    setIsSeeked(false);
    setIsPlaying(true);
    setIsSourceUnloaded(false);
    onPlaying();
  }, [onPlaying]);

  const handlePause = useCallback(() => {
    const updateState = () => {
      pauseTimeout.current = undefined;
      setIsPlaying(false);
    };
    pauseTime.current = Date.now();
    if (pauseTimeout.current) {
      clearTimeout(pauseTimeout.current);
      updateState();
    }
    pauseTimeout.current = setTimeout(updateState, TIME_TO_WAIT_ON_PAUSE);
    isPlayingRef.current = false;
    onPause();
  }, [onPause]);

  const handleProgress = useCallback((newProgress) => {
    progressRef.current = newProgress;
    if (pauseOnShowControls && isLive && isPlayingRef.current) return;
    onProgress(newProgress);
  }, [isLive, pauseOnShowControls, onProgress]);

  const handleDurationChange = useCallback((newDuration) => {
    onDurationChange(newDuration);
  }, [onDurationChange]);

  const handleBuffering = useCallback(() => {
    setIsBuffering(true);
    onBuffering();
  }, [onBuffering]);

  const handleBufferingEnded = useCallback(() => {
    setIsBuffering(false);
    onBufferingEnded();
  }, [onBufferingEnded]);

  const handleSeeked = useCallback(() => {
    setIsSeeked(true);
    onSeeked();
  }, [onSeeked]);

  const handleSourceUnloaded = useCallback(() => {
    setIsSourceUnloaded(true);
    setIsPlaying(false);
  }, []);

  const handleToggleMiniEpg = useCallback((displayingMiniEpg) => {
    isDisplayingMiniEpg.current = displayingMiniEpg;
  }, []);

  const manifestUrl = source?.dash;

  const { onResponseReceived, onRequest } = useMemo(() => (hasTokenization({ manifestUrl })
    ? getCDNTokenizationConfig(['queryParam'])
    : {}), [manifestUrl]);

  const handleRequest = useCallback(async (type, request) => {
    if (type === PlayerRequestType.LICENSE && logLicenseEvents) {
      logLicenseEvent('request', request.body);
    }
    if (onRequest) return onRequest(type, request);
    return {};
  }, [logLicenseEvents, onRequest]);

  const handleResponse = useCallback(async (type, response) => {
    if (type === PlayerRequestType.LICENSE && logLicenseEvents) {
      logLicenseEvent('response', response.data);
    }
    if (onResponseReceived) return onResponseReceived(type, response);
    return {};
  }, [logLicenseEvents, onResponseReceived]);

  const handleError = useCallback((error) => {
    console.error('Player reported error', error);
    onError(error);
  }, [onError]);

  const handleWarning = useCallback((error) => {
    console.error('Player reported warning', error);
  }, []);

  const handleFailedLicenseError = useCallback((error) => {
    console.error('Player reported license failed error', error);
    if (onLicenseFailed) {
      onLicenseFailed(error);
    } else {
      onError(error);
    }
  }, [onLicenseFailed, onError]);

  const handleHDCPError = useCallback((error) => {
    console.error('Player reported HDCP error', error);
    if (onHDCPError) {
      onHDCPError(error);
    } else {
      onError(error);
    }
  }, [onHDCPError, onError]);

  const handleNonPlayableSources = useCallback((error) => {
    console.error('Player reported non playable sources', error);
    if (!nextSource()) {
      const mutableError = error;
      mutableError.isSourceError = true;
      onError(mutableError);
    }
  }, [nextSource, onError]);

  const processAudioTracks = useMemo(
    () => createProcessAudioTracks(audioTracksFilter),
    [audioTracksFilter],
  );

  const handleSourceLoaded = useCallback(({
    audioTracks: audios,
    subtitleTracks: availableSubtitles,
  }) => {
    const parsedAudioTracks = processAudioTracks(audios);
    setAudioTracks(parsedAudioTracks);
    const { audio, subtitles } = preference;

    if (audio) {
      const matchingAudio = getMatchingAudio(audio, parsedAudioTracks);

      if (matchingAudio) {
        playerRef.current.selectAudioTrack(matchingAudio);
      }
    }

    // Defines a subtitle with the user's language when there is no audio option in your language
    if (!subtitles) {
      const languageUser = isBrazil ? Locale.PT : Locale.ES;
      const hasAudioForLanguageUser = hasAudioTrackByLanguage(audios, languageUser);
      const subtitleForLanguageUser = getSubtitleTrackByLanguage(availableSubtitles, languageUser);

      if (!hasAudioForLanguageUser && subtitleForLanguageUser) {
        playerRef.current.selectSubtitleTrack(subtitleForLanguageUser);
      }
    }
  }, [processAudioTracks, preference, isBrazil]);

  const handleTracksEvent = useCallback(({ event, param }) => {
    switch (event) {
      case PLAYER_TRACKS_EVENTS.SET_AVAILABLE_AUDIO_LANGUAGES: {
        const parsedAudioTracks = processAudioTracks(param);
        setAudioTracks(parsedAudioTracks);
        break;
      }
      case PLAYER_TRACKS_EVENTS.SET_AVAILABLE_SUBTITLE_TRACKS: {
        const { subtitles } = preference;
        setSubtitleTracks(Tracks.addIndexToUnknownTracks(Tracks.parseTextTracks(param)));
        if (subtitles) {
          const matchingSubtitle = getMatchingSubtitle(subtitles, param);
          if (matchingSubtitle) {
            playerRef.current.selectSubtitleTrack(matchingSubtitle);
          }
        }
        break;
      }
      default: {
        break;
      }
    }
  }, [preference, processAudioTracks]);

  const updateSubtitles = useCallback(() => {
    const param = playerRef.current.getAvailableSubtitles();
    if (!param || param.length === 0) return;
    handleTracksEvent({
      event: PLAYER_TRACKS_EVENTS.SET_AVAILABLE_SUBTITLE_TRACKS,
      param,
    });
  }, [handleTracksEvent]);

  const updateAudio = useCallback(() => {
    const param = playerRef.current.getAvailableAudio();
    if (!param || param.length === 0) return;
    handleTracksEvent({
      event: PLAYER_TRACKS_EVENTS.SET_AVAILABLE_AUDIO_LANGUAGES,
      param,
    });
  }, [handleTracksEvent]);

  const sourceToUseSelected = isDrmAgentLoaded ? sourceToUse : undefined;

  const reset = useCallback(() => {
    setIsPlaying(false);
    setIsSeeked(false);
    progressRef.current = savedProgress || 0;
    resetIsReadyToPlay();
    setSourceToUse(undefined);
    playbackStarted.current = false;
    tagsMountRef.current = false;
  }, [resetIsReadyToPlay, savedProgress]);

  useImperativeHandle(ref, () => ({
    unload: () => {
      playerRef.current?.unloadSource();
    },
  }), []);

  // Set progress to savedProgress when it is ready
  useEffect(() => {
    progressRef.current = savedProgress || 0;
  }, [savedProgress]);

  // When playable content changed (different episode or different station),
  // we need to reset player state.
  useEffect(() => {
    reset();
  }, [reset, sources]);

  // Update isPlayingRef when isPlaying changed
  useEffect(() => {
    isPlayingRef.current = isPlaying;
  }, [isPlaying]);

  // Pause content when disconnected message is shown and resume when is no longer displayed
  useEffect(() => {
    if (showDisconnectedMessage) {
      playingOnDisconnect.current = isPlayingRef.current;
      if (playingOnDisconnect.current) {
        playerRef.current.pause();
      }
    } else if (playingOnDisconnect.current) {
      playerRef.current.play();
    }
  }, [showDisconnectedMessage]);

  // Set sourceToUse once the request to fetch the content is completed
  useEffect(() => {
    if (isContentRequestCompleted) {
      setSourceToUse(source);
    }
  }, [isContentRequestCompleted, source]);

  useEffect(() => {
    if (isFromInterstitial && isPlaying && !playbackAfterInterstitialRef.current) {
      if (isCurrentInterstitialSchedule) {
        gaService.sendEvent({
          action: REPORT_GA.GA_EVENT_ACTION_BUTTON_CLICK,
          label: REPORT_GA.GA_EVENT_LABEL_AFTER_EVENT_INTERSTITIAL,
          category: REPORT_GA.GA_EVENT_CATEGORY_INTERSTITIAL,
        });
      } else {
        gaService.sendEvent({
          action: REPORT_GA.GA_EVENT_ACTION_BUTTON_CLICK,
          label: REPORT_GA.GA_EVENT_LABEL_BEFORE_EVENT_INTERSTITIAL,
          category: REPORT_GA.GA_EVENT_CATEGORY_INTERSTITIAL,
        });
      }
      playbackAfterInterstitialRef.current = true;
    }
  }, [isCurrentInterstitialSchedule, isFromInterstitial, isPlaying]);

  useEffect(() => {
    hideLoading();
  }, [hideLoading]);

  // Set shouldPlay when isReadyToPlay changed
  useEffect(() => {
    setShouldPlay(
      !isParentalBlocked && isReadyToPlay && !(pauseOnShowControls && isDisplayingMiniEpg.current),
    );
  }, [isReadyToPlay, pauseOnShowControls, isParentalBlocked]);

  const savedProgressToUse = isLive ? undefined : savedProgress;

  const progressOffset = useMemo(() => {
    const progress = startOffset ?? savedProgressToUse;

    if (!content?.duration) return progress;

    if (progress >= (content?.duration ?? 0) * 0.95) {
      return 0;
    }

    return progress;
  }, [content?.duration, savedProgressToUse, startOffset]);

  /* Interactivity Button Tappp Animation */
  useEffect(() => () => {
    const hasUserViewedTheInteractivityButton = JSON.parse(
      sessionStorage.getItem(PROFILE_VIEWED_INTERACTIVITY_BUTTON),
    );
    if (hasUserViewedTheInteractivityButton) {
      if (profileStoraged) {
        if (profileStoraged?.viewedButton < 3) {
          const newProfilesAlreadyViewedActivityButton = JSON.stringify(
            profilesAlreadyViewedActivityButton.map((profile) => {
              if (profile.id === profileStoraged.id) {
                return {
                  ...profileStoraged,
                  viewedButton: (profileStoraged?.viewedButton || 0) + 1,
                };
              }

              return profile;
            }),
          );

          localStorage.setItem(
            PROFILES_WERE_SHOWN_INTERACTIVITY_BUTTON,
            newProfilesAlreadyViewedActivityButton,
          );
        }
      } else {
        const newProfile = {
          id: profileSelected.id,
          viewedButton: 1,
        };

        const newProfilesAlreadyViewedActivityButton = JSON.stringify(
          [...profilesAlreadyViewedActivityButton, newProfile],
        );

        localStorage.setItem(
          PROFILES_WERE_SHOWN_INTERACTIVITY_BUTTON,
          newProfilesAlreadyViewedActivityButton,
        );
      }

      sessionStorage.removeItem(PROFILE_VIEWED_INTERACTIVITY_BUTTON);
    }
  }, [profileSelected, profileStoraged, profilesAlreadyViewedActivityButton]);

  return (
    <ShakaPlayer
      className={`dtv-html5-c-player ${reducedSize ? 'dtv-html5-c-player--reduced-size' : ''}`}
      readyToBuild={!isFetchingConfig && !isLoadingPlayerConfig}
      isLive={isLive}
      content={content}
      channelName={channelName}
      bufferConfig={sourceToUseSelected?.isLowLatency
        ? lowLatencyBufferConfig
        : (bufferOverrides || bufferConfig)}
      presentationDelay={presentationDelay}
      restrictions={restrictions}
      abrRestrictions={abrRestrictions}
      lowLatency={sourceToUseSelected?.isLowLatency ? lowLatency : undefined}
      keySystemsConfig={newKeySystemsConfig}
      source={sourceToUseSelected}
      shouldPlay={shouldPlay}
      startOffset={progressOffset}
      recovery={recovery}
      stallMechanism={stallMechanism}
      shouldReloadOnError={shouldReloadOnError}
      npawAdapterOptions={npawAdapterOptions}
      onReadyToPlay={handleReadyToPlay}
      onPlay={handlePlay}
      onPlaying={handlePlaying}
      onPause={handlePause}
      onProgress={handleProgress}
      onEnded={onEnded}
      onDurationChange={handleDurationChange}
      onBuffering={handleBuffering}
      onBufferingEnded={handleBufferingEnded}
      onSeeked={handleSeeked}
      onHDCPError={handleHDCPError}
      onUnrecoverableError={handleError}
      onFailedLicense={handleFailedLicenseError}
      onNonPlayableSources={handleNonPlayableSources}
      onSourceLoaded={handleSourceLoaded}
      onSourceUnloaded={handleSourceUnloaded}
      onTracksEvent={handleTracksEvent}
      onWarning={handleWarning}
      singleAudioTrackLanguage={singleAudioTrackLanguage}
      onResponseReceived={handleResponse}
      onRequest={handleRequest}
      ref={playerRef}
    >
      {useMemo(() => {
        const children = (
          <PlayerUI
            className={className}
            isLive={isLive}
            isBetAllowed={isBetAllowed}
            showButtonAnimation={showButtonAnimation}
            isReadyToPlay={isReadyToPlay}
            isPlaying={isPlaying}
            isSeeked={isSeeked}
            isBuffering={isBuffering}
            isSourceUnloaded={isSourceUnloaded}
            audioTracks={audioTracks}
            subtitleTracks={subtitleTracks}
            updateAudio={updateAudio}
            updateSubtitles={updateSubtitles}
            hasNextEpisode={hasNextEpisode}
            schedule={schedule}
            content={content}
            isContentRequestCompleted={isContentRequestCompleted}
            channelId={channelId}
            play={play}
            pause={pause}
            setProgress={scrubTo}
            selectAudioTrack={selectAudioTrack}
            selectSubtitleTrack={selectSubtitleTrack}
            disableSubtitles={disableSubtitles}
            goToNextEpisode={goToNextEpisode}
            goToLive={goToLive}
            isFromInterstitial={isFromInterstitial}
            homeOnBack={homeOnBack}
            onUserInteraction={onUserInteraction}
            counterEpisode={counterEpisode}
            setPlayerReducedSize={setReducedSize}
            pauseOnShowControls={pauseOnShowControls}
            onToggleMiniEpg={handleToggleMiniEpg}
            channelName={channelName}
            playerCommonTagEvent={playerCommonTagEvent}
            playerUpsellMessage={upsellMessage}
            playerUpsellLink={upsellLink}
            hasPlaybackAuthorizerSuccess={hasPlaybackAuthorizerSuccess}
          />
        );
        const wrappedChildren = childrenWrapper ? childrenWrapper(children) : children;

        return (
          <>
            {childrenProp}
            {wrappedChildren}
          </>
        );
      }, [
        audioTracks,
        channelId,
        channelName,
        childrenProp,
        childrenWrapper,
        className,
        content,
        counterEpisode,
        disableSubtitles,
        goToLive,
        goToNextEpisode,
        handleToggleMiniEpg,
        hasNextEpisode,
        homeOnBack,
        isBuffering,
        isContentRequestCompleted,
        isFromInterstitial,
        isLive,
        isBetAllowed,
        showButtonAnimation,
        isPlaying,
        isReadyToPlay,
        isSeeked,
        isSourceUnloaded,
        onUserInteraction,
        pause,
        pauseOnShowControls,
        play,
        playerCommonTagEvent,
        schedule,
        scrubTo,
        selectAudioTrack,
        selectSubtitleTrack,
        subtitleTracks,
        upsellMessage,
        upsellLink,
        hasPlaybackAuthorizerSuccess,
        updateAudio,
        updateSubtitles,
      ])}
    </ShakaPlayer>
  );
}

Player.propTypes = {
  // Own props
  sources: PropTypes.arrayOf(
    PropTypes.shape({
      dash: PropTypes.string,
      hls: PropTypes.string,
      smooth: PropTypes.string,
    }),
  ),
  drmKeySystem: PropTypes.oneOf([
    DrmKeySystem.PLAYREADY,
    DrmKeySystem.WIDEVINE,
  ]).isRequired,
  isLive: PropTypes.bool,
  savedProgress: PropTypes.number,
  playSource: PropTypes.string,
  className: PropTypes.string,
  schedule: PropTypes.shape({}),
  content: PropTypes.shape({}),
  isContentRequestCompleted: PropTypes.bool,
  bufferConfig: PropTypes.objectOf(PropTypes.number),
  channelId: PropTypes.string,
  channelName: PropTypes.string,
  counterEpisode: PropTypes.number,
  hasNextEpisode: PropTypes.bool,
  goToNextEpisode: PropTypes.func,
  shouldReloadOnError: PropTypes.func,
  onUserInteraction: PropTypes.func,
  onPlaying: PropTypes.func,
  onPause: PropTypes.func,
  onProgress: PropTypes.func,
  onSeeked: PropTypes.func,
  onDurationChange: PropTypes.func,
  onBuffering: PropTypes.func,
  onBufferingEnded: PropTypes.func,
  onEnded: PropTypes.func,
  onHDCPError: PropTypes.func,
  onLicenseFailed: PropTypes.func,
  onError: PropTypes.func,
  isFromInterstitial: PropTypes.bool,
  homeOnBack: PropTypes.bool,
  isCurrentInterstitialSchedule: PropTypes.bool,
  childrenWrapper: PropTypes.func,
  setPlayerReadyToPlay: PropTypes.func,
  onGoLive: PropTypes.func,
  children: PropTypes.node,
  hasPlaybackAuthorizerSuccess: PropTypes.bool,
};

export default memo(forwardRef(Player));
